import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Banknote,
  Calendar as CalendarIcon,
  CheckCircle2,
  ArrowLeftRight,
  Clock,
  Sparkles,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
import { loansService } from "../services/api";
import {
  toPersianNumbers,
  toEnglishNumbers,
  formatCurrency,
} from "../utils/numbers";
import JalaliDatePicker from "../components/JalaliDatePicker";
import { Modal } from "../components/Modal";
import { compressImage } from "../utils/imageCompressor";

interface Loan {
  id: number;
  user_id: number;
  amount: string | number;
  requested_date: string;
  description: string;
  collateral?: string;
  status:
    | "pending"
    | "waiting_user_approval"
    | "user_approved"
    | "approved_registration"
    | "fee_paid"
    | "rejected"
    | "paid"
    | "cancelled";
  registration_fee_amount?: string | number;
  registration_fee_percentage?: number;
  receipt_image?: string;
  first_name: string;
  last_name: string;
  national_code: string;
  mobile?: string;
  loan_type_name?: string;
  percentage?: number;
  installments_count?: number;
  requested_percentage?: number;
  requested_installments_count?: number;
  admin_notes?: string;
  user_notes?: string;
  negotiation_history?: string;
  installments?: any[];
  is_settled?: boolean;
}

const getCollateralText = (c?: string) => {
  if (c === "gold") return "طلا";
  if (c === "simcard") return "سیم کارت";
  if (c === "dollar") return "دلار";
  if (c === "document") return "سند ملکی";
  if (c === "credit") return "اعتبار صندوق";
  return c || "ندارد";
};

const getStatusBadge = (loan: Loan) => {
  const isSettled =
    loan.is_settled ||
    (loan.status === "paid" &&
      Boolean(loan.installments && loan.installments.length > 0) &&
      loan.installments!.every((i) => i.status === "paid"));

  if (isSettled) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
        تسویه شده کامل
      </span>
    );
  }

  switch (loan.status) {
    case "pending":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
          در انتظار بررسی مدیر
        </span>
      );
    case "waiting_user_approval":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
          پیشنهاد شرایط به متقاضی
        </span>
      );
    case "user_approved":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
          تایید شده توسط متقاضی
        </span>
      );
    case "approved_registration":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
          تایید ثبت‌نام (در انتظار کارمزد)
        </span>
      );
    case "fee_paid":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border border-teal-200 dark:border-teal-800">
          تسویه کارمزد (در صف پرداخت وام)
        </span>
      );
    case "paid":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
          وام پرداخت شده (در حال بازپرداخت)
        </span>
      );
    case "rejected":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
          رد شده
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          لغو شده
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
          در انتظار بررسی
        </span>
      );
  }
};

export default function Loans() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedLoanForReceipt, setSelectedLoanForReceipt] =
    useState<Loan | null>(null);
  const [receiptImageFile, setReceiptImageFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Modal State
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";
  const isInvestor = user?.role === "investor";
  const isInvestorOrAdmin = isAdmin || isInvestor;

  const [activeTab, setActiveTab] = useState<"my" | "others">("my");
  const [othersLoans, setOthersLoans] = useState<Loan[]>([]);
  const [loadingOthers, setLoadingOthers] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    amount: "",
    requested_date: "",
    description: "",
    collateral: "credit",
    percentage: "",
    installments_count: "",
  });
  const [availableDate, setAvailableDate] = useState<string | null>(null);
  const [availabilityInfo, setAvailabilityInfo] = useState<{
    reserveRequired?: number;
    singleAccountValue?: number;
    requiredBalance?: number;
    currentBalance?: number;
  } | null>(null);
  const [isCheckingAmount, setIsCheckingAmount] = useState(false);

  // User Review / Counter-proposal Modal State
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedLoanForReview, setSelectedLoanForReview] =
    useState<Loan | null>(null);
  const [reviewMode, setReviewMode] = useState<"accept" | "counter">("accept");
  const [counterData, setCounterData] = useState({
    percentage: "",
    installments_count: "",
    notes: "",
  });
  const [acceptNotes, setAcceptNotes] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleOpenReviewModal = (loan: Loan) => {
    const currentUserId = user?.id;
    const currentUserNationalCode = user?.national_code;
    const isOwner =
      (currentUserId && loan.user_id === currentUserId) ||
      (currentUserNationalCode &&
        loan.national_code === currentUserNationalCode);

    if (!isOwner) {
      setModalState({
        isOpen: true,
        title: "عدم دسترسی",
        message: "شما تنها مجاز به بررسی درخواست وام‌های خودتان هستید.",
        type: "error",
      });
      return;
    }

    setSelectedLoanForReview(loan);
    setReviewMode("accept");
    setAcceptNotes("");
    setCounterData({
      percentage:
        loan.requested_percentage != null
          ? String(loan.requested_percentage)
          : loan.percentage != null
            ? String(loan.percentage)
            : "",
      installments_count:
        loan.requested_installments_count != null
          ? String(loan.requested_installments_count)
          : loan.installments_count != null
            ? String(loan.installments_count)
            : "12",
      notes: "",
    });
    setIsReviewModalOpen(true);
  };

  const handleConfirmAccept = async () => {
    if (!selectedLoanForReview) return;
    setSubmittingReview(true);
    try {
      await loansService.respondToLoan(selectedLoanForReview.id, {
        action: "accept",
        notes: acceptNotes,
      });
      setIsReviewModalOpen(false);
      fetchLoans();
      setModalState({
        isOpen: true,
        title: "موفق",
        message:
          "شرایط پیشنهادی مدیر توسط شما تایید شد و جهت تایید نهایی برای مدیریت ارسال گردید.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در تایید شرایط",
        type: "error",
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleConfirmCounter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLoanForReview) return;
    const perc = Number(toEnglishNumbers(counterData.percentage));
    const inst = parseInt(toEnglishNumbers(counterData.installments_count), 10);
    if (isNaN(perc) || perc < 0 || perc > 15) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "درصد کارمزد پیشنهادی باید بین ۰ تا ۱۵ درصد باشد.",
        type: "error",
      });
      return;
    }
    if (isNaN(inst) || inst < 1 || inst > 36) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "تعداد اقساط پیشنهادی باید بین ۱ تا ۳۶ ماه باشد.",
        type: "error",
      });
      return;
    }
    setSubmittingReview(true);
    try {
      await loansService.respondToLoan(selectedLoanForReview.id, {
        action: "counter",
        percentage: perc,
        installments_count: inst,
        notes: counterData.notes,
      });
      setIsReviewModalOpen(false);
      fetchLoans();
      setModalState({
        isOpen: true,
        title: "موفق",
        message: "پیشنهاد متقابل جدید شما با موفقیت برای مدیریت ارسال گردید.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در ارسال پیشنهاد متقابل",
        type: "error",
      });
    } finally {
      setSubmittingReview(false);
    }
  };
  const [amountError, setAmountError] = useState<string | null>(null);

  useEffect(() => {
    const checkAmount = async () => {
      const amount = Number(formData.amount);
      if (!amount || amount <= 0) {
        setAvailableDate(null);
        setAvailabilityInfo(null);
        setAmountError(null);
        return;
      }
      setIsCheckingAmount(true);
      setAmountError(null);
      try {
        const res = await loansService.getAvailableDate(amount);
        if (res.not_possible) {
          setAmountError(res.message);
          setAvailableDate(null);
          setAvailabilityInfo(null);
        } else {
          setAvailableDate(res.available_date);
          setAvailabilityInfo({
            reserveRequired: res.reserve_required,
            singleAccountValue: res.single_account_value,
            requiredBalance: res.required_balance,
            currentBalance: res.current_balance,
          });
          if (
            formData.requested_date &&
            new Date(formData.requested_date) < new Date(res.available_date)
          ) {
            setFormData((prev) => ({ ...prev, requested_date: "" }));
          }
        }
      } catch (err: any) {
        setAmountError(err.response?.data?.message || "خطا در بررسی مبلغ.");
        setAvailabilityInfo(null);
      } finally {
        setIsCheckingAmount(false);
      }
    };

    const delay = setTimeout(checkAmount, 600);
    return () => clearTimeout(delay);
  }, [formData.amount]);

  useEffect(() => {
    fetchLoans();
    if (isInvestorOrAdmin) {
      fetchOthersLoans();
    }
  }, [isInvestorOrAdmin]);

  const handleOpenReceiptModal = (loan: Loan) => {
    setSelectedLoanForReceipt(loan);
    setReceiptImageFile(null);
    setIsReceiptModalOpen(true);
  };

  const handleUploadReceipt = async () => {
    if (!selectedLoanForReceipt || !receiptImageFile) return;

    setUploadingReceipt(true);
    try {
      const base64Image = await compressImage(receiptImageFile, 800, 800, 0.7);
      await loansService.uploadReceipt(selectedLoanForReceipt.id, base64Image);

      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message: "فیش با موفقیت آپلود شد.",
        type: "success",
      });
      setIsReceiptModalOpen(false);
      fetchLoans();
    } catch (error: any) {
      console.error("Error uploading receipt:", error);
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error?.response?.data?.message || "خطا در آپلود فیش",
        type: "error",
      });
    } finally {
      setUploadingReceipt(false);
    }
  };

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await loansService.getLoans({ my_loans: true });
      let loansArray = Array.isArray(response) ? response : response.data || [];

      const currentUserId = user?.id;
      const currentUserNationalCode = user?.national_code;
      loansArray = loansArray.filter(
        (loan: Loan) =>
          (currentUserId && loan.user_id === currentUserId) ||
          (currentUserNationalCode &&
            loan.national_code === currentUserNationalCode),
      );

      setLoans(loansArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching loans:", error);
      setLoans([]);
      setLoading(false);
    }
  };

  const fetchOthersLoans = async () => {
    setLoadingOthers(true);
    try {
      const response = await loansService.getLoans();
      let loansArray = Array.isArray(response) ? response : response.data || [];
      const currentUserId = user?.id;
      const currentUserNationalCode = user?.national_code;
      const others = loansArray.filter(
        (loan: Loan) =>
          loan.user_id !== currentUserId &&
          (!currentUserNationalCode ||
            loan.national_code !== currentUserNationalCode),
      );
      setOthersLoans(others);
    } catch (error) {
      console.error("Error fetching others loans:", error);
      setOthersLoans([]);
    } finally {
      setLoadingOthers(false);
    }
  };

  const handleAddLoan = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || !formData.requested_date) {
      setModalState({
        isOpen: true,
        title: "خطا در اطلاعات ورودی",
        message: "لطفا مبلغ و تاریخ را وارد کنید.",
        type: "error",
      });
      return;
    }

    if (formData.percentage === "" || formData.installments_count === "") {
      setModalState({
        isOpen: true,
        title: "خطا در اطلاعات ورودی",
        message: "لطفاً درصد کارمزد و تعداد اقساط مورد نظر خود را وارد کنید.",
        type: "error",
      });
      return;
    }

    const percentageNum = Number(formData.percentage);
    const installmentsNum = parseInt(formData.installments_count, 10);

    if (isNaN(percentageNum) || percentageNum < 0 || percentageNum > 100) {
      setModalState({
        isOpen: true,
        title: "خطا در اطلاعات ورودی",
        message: "درصد کارمزد وارد شده باید یک عدد معتبر باشد.",
        type: "error",
      });
      return;
    }

    if (isNaN(installmentsNum) || installmentsNum < 1 || installmentsNum > 60) {
      setModalState({
        isOpen: true,
        title: "خطا در اطلاعات ورودی",
        message: "تعداد اقساط باید بین ۱ تا ۶۰ ماه باشد.",
        type: "error",
      });
      return;
    }

    if (amountError) {
      return;
    }

    try {
      await loansService.createLoan({
        amount: Number(formData.amount),
        requested_date: formData.requested_date,
        description: formData.description,
        collateral: formData.collateral,
        percentage: percentageNum,
        installments_count: installmentsNum,
      });
      fetchLoans();
      setIsAddModalOpen(false);
      setFormData({
        amount: "",
        requested_date: "",
        description: "",
        collateral: "credit",
        percentage: "",
        installments_count: "",
      });
      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message: "درخواست وام با موفقیت ثبت شد.",
        type: "success",
      });
    } catch (error) {
      console.error("Error creating loan:", error);
      setModalState({
        isOpen: true,
        title: "خطا در ثبت اطلاعات",
        message:
          (error as any).response?.data?.message ||
          "خطا در ثبت درخواست وام. لطفا دوباره تلاش کنید.",
        type: "error",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            ثبت نام اولیه وام‌ها
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {activeTab === "my"
              ? "لیست درخواست‌های وام شما"
              : "مشاهده ثبت‌نام‌های اولیه وام سایر اعضا (حالت فقط مشاهده)"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!isAdmin && isInvestorOrAdmin && (
            <span className="px-3.5 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
              حالت فقط مشاهده (سرمایه‌گذار)
            </span>
          )}

          {activeTab === "my" && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex text-center justify-center items-center gap-2 bg-persian-600 hover:bg-persian-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-lg shadow-persian-900/20 dark:shadow-none cursor-pointer"
            >
              <Plus className="w-5 h-5" />
              <span className="text-center justify-center">
                ثبت نام اولیه وام جدید
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs for investors and admins */}
      {isInvestorOrAdmin && (
        <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700/80 w-fit">
          <button
            onClick={() => setActiveTab("my")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === "my"
                ? "bg-white dark:bg-slate-700 text-persian-600 dark:text-persian-300 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
            }`}
          >
            <span>درخواست‌های وام من</span>
            <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
              {toPersianNumbers(loans.length)}
            </span>
          </button>

          <button
            onClick={() => {
              setActiveTab("others");
              fetchOthersLoans();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === "others"
                ? "bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-300 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
            }`}
          >
            <span>ثبت‌نام اولیه وام‌های دیگران</span>
            <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
              {toPersianNumbers(othersLoans.length)}
            </span>
          </button>
        </div>
      )}

      {activeTab === "my" && (
        <>
          {loans.some((l) => l.status === "waiting_user_approval") && (
            <div className="p-4 bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-blue-500/10 border border-purple-200 dark:border-purple-800/60 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    نظر مدیریت برای درخواست وام شما اعلام شد!
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                    مدیر سامانه شرایط وام (کارمزد و اقساط) را مشخص نموده است.
                    لطفاً شرایط را بررسی و تایید نمایید یا پیشنهاد جدید ثبت
                    کنید.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  const waiting = loans.find(
                    (l) => l.status === "waiting_user_approval",
                  );
                  if (waiting) handleOpenReviewModal(waiting);
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                مشاهده و پاسخ
              </button>
            </div>
          )}

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-right border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      مبلغ درخواستی
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      نوع وام
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      کارمزد و اقساط
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      تاریخ درخواستی
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      وثیقه
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      وضعیت
                    </th>
                    <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      عملیات
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
                          <p>در حال دریافت اطلاعات وام‌ها...</p>
                        </div>
                      </td>
                    </tr>
                  ) : loans.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        درخواستی یافت نشد.
                      </td>
                    </tr>
                  ) : (
                    loans.map((loan) => (
                      <tr
                        key={loan.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                            <Banknote className="w-4 h-4 text-slate-400" />
                            {formatCurrency(loan.amount)}
                          </div>
                        </td>
                        <td className="p-4 text-slate-700 dark:text-slate-300">
                          {loan.loan_type_name || "-"}
                        </td>
                        <td className="p-4">
                          {loan.status === "waiting_user_approval" ? (
                            <div className="flex flex-col gap-1 text-sm">
                              <span className="font-semibold text-purple-700 dark:text-purple-300">
                                پیشنهاد مدیر:{" "}
                                {toPersianNumbers(loan.percentage ?? 0)}٪ -{" "}
                                {toPersianNumbers(loan.installments_count ?? 0)}{" "}
                                ماه
                              </span>
                              {loan.admin_notes && (
                                <span className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1">
                                  نظر مدیر: {loan.admin_notes}
                                </span>
                              )}
                            </div>
                          ) : loan.status === "user_approved" ? (
                            <div className="flex flex-col gap-1 text-sm">
                              <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                                تایید شما:{" "}
                                {toPersianNumbers(loan.percentage ?? 0)}٪ -{" "}
                                {toPersianNumbers(loan.installments_count ?? 0)}{" "}
                                ماه
                              </span>
                              {loan.user_notes && (
                                <span className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1">
                                  پیام شما: {loan.user_notes}
                                </span>
                              )}
                            </div>
                          ) : loan.percentage != null ||
                            loan.requested_percentage != null ? (
                            <div className="flex flex-col gap-1 text-slate-700 dark:text-slate-300 text-sm">
                              <span>
                                {toPersianNumbers(
                                  loan.percentage ??
                                    loan.requested_percentage ??
                                    0,
                                )}
                                ٪ -{" "}
                                {toPersianNumbers(
                                  loan.installments_count ??
                                    loan.requested_installments_count ??
                                    0,
                                )}{" "}
                                ماه
                              </span>
                              {(loan.status === "approved_registration" ||
                                loan.status === "fee_paid") &&
                                loan.registration_fee_amount && (
                                  <span className="text-xs text-persian-600 dark:text-persian-400 font-medium whitespace-nowrap">
                                    کارمزد اولیه:{" "}
                                    {formatCurrency(
                                      loan.registration_fee_amount,
                                    )}
                                  </span>
                                )}
                              {loan.status === "pending" && (
                                <span className="text-[11px] text-slate-400">
                                  {loan.user_notes
                                    ? "پیشنهاد متقابل کاربر"
                                    : "پیشنهاد کاربر"}
                                </span>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                            <CalendarIcon className="w-4 h-4 text-slate-400" />
                            {toPersianNumbers(loan.requested_date)}
                          </div>
                        </td>
                        <td className="p-4 text-slate-700 dark:text-slate-300">
                          {loan.collateral === "gold"
                            ? "طلا"
                            : loan.collateral === "simcard"
                              ? "سیم کارت"
                              : loan.collateral === "dollar"
                                ? "دلار"
                                : loan.collateral === "document"
                                  ? "سند"
                                  : loan.collateral === "credit"
                                    ? "اعتبار صندوق"
                                    : "-"}
                        </td>
                        <td className="p-4">
                          {(() => {
                            const isSettled =
                              loan.is_settled ||
                              (loan.status === "paid" &&
                                Boolean(
                                  loan.installments &&
                                  loan.installments.length > 0,
                                ) &&
                                loan.installments!.every(
                                  (i) => i.status === "paid",
                                ));

                            return (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  isSettled
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800"
                                    : loan.status === "waiting_user_approval"
                                      ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                                      : loan.status === "user_approved"
                                        ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                                        : loan.status ===
                                            "approved_registration"
                                          ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                          : loan.status === "fee_paid"
                                            ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                                            : loan.status === "paid"
                                              ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                              : loan.status === "rejected"
                                                ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                                : loan.status === "cancelled"
                                                  ? "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                                  : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                }`}
                              >
                                {isSettled
                                  ? "تسویه شده (پایان اقساط)"
                                  : loan.status === "waiting_user_approval"
                                    ? "نظر مدیر (نیاز به اقدام شما)"
                                    : loan.status === "user_approved"
                                      ? "تایید شما (در انتظار تایید نهایی مدیر)"
                                      : loan.status === "approved_registration"
                                        ? "منتظر پرداخت کارمزد"
                                        : loan.status === "fee_paid"
                                          ? "کارمزد پرداخت شده (منتظر وام)"
                                          : loan.status === "paid"
                                            ? "وام دریافت شد"
                                            : loan.status === "rejected"
                                              ? "رد شده"
                                              : loan.status === "cancelled"
                                                ? "لغو شده"
                                                : "در انتظار بررسی مدیر"}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-4">
                          {loan.status === "waiting_user_approval" && (
                            <button
                              onClick={() => handleOpenReviewModal(loan)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all text-xs font-semibold shadow-sm hover:shadow cursor-pointer"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              بررسی پیشنهاد مدیر
                            </button>
                          )}
                          {loan.status === "user_approved" && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                              <Clock className="w-3.5 h-3.5" />
                              منتظر تایید نهایی مدیر
                            </span>
                          )}
                          {loan.status === "approved_registration" && (
                            <button
                              onClick={() => handleOpenReceiptModal(loan)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 rounded-lg hover:bg-persian-100 dark:hover:bg-persian-900/50 transition-colors text-sm font-medium cursor-pointer"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                ></path>
                              </svg>
                              آپلود فیش
                            </button>
                          )}
                          {loan.status === "fee_paid" && loan.receipt_image && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium whitespace-nowrap">
                              فیش ارسال شده
                            </span>
                          )}
                          {loan.status === "paid" && (
                            <button
                              onClick={() => navigate("/loan-history")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-persian-600 bg-persian-50 hover:bg-persian-100 dark:text-persian-400 dark:bg-persian-900/30 dark:hover:bg-persian-900/50 rounded-lg transition-colors border border-persian-200 dark:border-persian-800"
                            >
                              مشاهده در تاریخچه
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === "others" && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                ثبت‌نام‌های اولیه وام سایر کاربران
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                {toPersianNumbers(othersLoans.length)} مورد
              </span>
            </div>
            <button
              onClick={fetchOthersLoans}
              disabled={loadingOthers}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loadingOthers ? "animate-spin" : ""}`}
              />
              <span>به‌روزرسانی</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse whitespace-nowrap">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300">
                  <th className="p-3.5 font-semibold">متقاضی</th>
                  <th className="p-3.5 font-semibold">مبلغ درخواستی</th>
                  <th className="p-3.5 font-semibold">نوع وام</th>
                  <th className="p-3.5 font-semibold">کارمزد و اقساط</th>
                  <th className="p-3.5 font-semibold">تاریخ درخواستی</th>
                  <th className="p-3.5 font-semibold">وثیقه</th>
                  <th className="p-3.5 font-semibold">وضعیت</th>
                  <th className="p-3.5 font-semibold text-center">دسترسی</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700 text-sm">
                {loadingOthers ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="w-7 h-7 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs">
                          در حال بارگذاری درخواست‌های دیگران...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : othersLoans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-8 text-center text-slate-500 text-xs"
                    >
                      هیچ درخواست وامی از سوی کاربران دیگر ثبت نشده است.
                    </td>
                  </tr>
                ) : (
                  othersLoans.map((loan) => (
                    <tr
                      key={loan.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="p-3.5">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {[loan.first_name, loan.last_name]
                              .filter(Boolean)
                              .join(" ") ||
                              (loan as any).full_name ||
                              (loan as any).name ||
                              (loan as any).username ||
                              `کاربر #${loan.user_id}`}
                          </span>
                          {(loan.national_code ||
                            loan.mobile ||
                            (loan as any).phone_number) && (
                            <span className="text-[11px] text-slate-400">
                              {loan.national_code
                                ? `کدملی: ${toPersianNumbers(loan.national_code)}`
                                : ""}
                              {loan.national_code &&
                              (loan.mobile || (loan as any).phone_number)
                                ? " | "
                                : ""}
                              {loan.mobile || (loan as any).phone_number
                                ? toPersianNumbers(
                                    loan.mobile || (loan as any).phone_number,
                                  )
                                : ""}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-200">
                          <Banknote className="w-4 h-4 text-emerald-600" />
                          {formatCurrency(loan.amount)}
                        </div>
                      </td>
                      <td className="p-3.5 text-slate-700 dark:text-slate-300">
                        {loan.loan_type_name || "-"}
                      </td>
                      <td className="p-3.5">
                        <div className="flex flex-col gap-0.5 text-xs text-slate-700 dark:text-slate-300">
                          <span>
                            {toPersianNumbers(
                              loan.percentage ?? loan.requested_percentage ?? 0,
                            )}
                            ٪ -{" "}
                            {toPersianNumbers(
                              loan.installments_count ??
                                loan.requested_installments_count ??
                                0,
                            )}{" "}
                            ماه
                          </span>
                          {loan.registration_fee_amount && (
                            <span className="text-[11px] text-persian-600 dark:text-persian-400 font-medium">
                              کارمزد اولیه:{" "}
                              {formatCurrency(loan.registration_fee_amount)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                          <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                          {loan.requested_date
                            ? toPersianNumbers(loan.requested_date)
                            : "-"}
                        </div>
                      </td>
                      <td className="p-3.5 text-xs text-slate-700 dark:text-slate-300">
                        {getCollateralText(loan.collateral)}
                      </td>
                      <td className="p-3.5">{getStatusBadge(loan)}</td>
                      <td className="p-3.5 text-center">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          فقط مشاهده
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                ثبت اولیه وام
              </h2>
            </div>

            <form onSubmit={handleAddLoan} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  مبلغ وام (ریال)
                </label>
                <input
                  type="text"
                  required
                  value={toPersianNumbers(
                    formData.amount
                      ? formData.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : "",
                  )}
                  onChange={(e) => {
                    const rawValue = toEnglishNumbers(
                      e.target.value.replace(/,/g, ""),
                    );
                    if (/^\d*$/.test(rawValue)) {
                      setFormData({ ...formData, amount: rawValue });
                    }
                  }}
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                  placeholder="مثلا ۱۰۰,۰۰۰,۰۰۰"
                />
                {isCheckingAmount && (
                  <p className="text-xs text-slate-500">
                    در حال بررسی امکان پرداخت...
                  </p>
                )}
                {amountError && (
                  <p className="text-xs text-red-500">{amountError}</p>
                )}
              </div>

              <div className="space-y-2 relative z-50">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  تاریخ درخواستی
                </label>
                {availableDate ? (
                  <div className="space-y-1 mb-2">
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                      اولین تاریخ مجاز:{" "}
                      {toPersianNumbers(
                        new Date(availableDate).toLocaleDateString("fa-IR"),
                      )}
                    </p>
                    {availabilityInfo?.reserveRequired ? (
                      <div className="text-[11px] text-slate-600 dark:text-slate-300 bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-lg p-2 leading-relaxed">
                        <span className="font-semibold text-amber-700 dark:text-amber-400">
                          شرط ذخیره صندوق:{" "}
                        </span>
                        پس از پرداخت این وام، حداقل ۲ برابر ارزش هر اکانت (
                        {toPersianNumbers(
                          formatCurrency(availabilityInfo.reserveRequired),
                        )}{" "}
                        ریال) باید در صندوق باقی بماند.
                      </div>
                    ) : null}
                  </div>
                ) : formData.amount && !amountError ? (
                  <p className="text-xs text-slate-500 mb-2">
                    در حال بارگذاری تاریخ‌های مجاز بر اساس موجودی و ذخیره
                    صندوق...
                  </p>
                ) : null}
                <div className="relative">
                  <JalaliDatePicker
                    value={formData.requested_date}
                    onChange={(date) =>
                      setFormData({ ...formData, requested_date: date })
                    }
                    placeholder="انتخاب تاریخ"
                    minDate={availableDate || undefined}
                  />
                  <CalendarIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  وثیقه
                </label>
                <select
                  required
                  value={formData.collateral}
                  onChange={(e) =>
                    setFormData({ ...formData, collateral: e.target.value })
                  }
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all appearance-none"
                >
                  <option value="credit">اعتبار صندوق</option>
                  <option value="gold">طلا</option>
                  <option value="simcard">سیم کارت</option>
                  <option value="dollar">دلار</option>
                  <option value="document">سند</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    درصد کارمزد پیشنهادی (٪)
                  </label>
                  <input
                    type="text"
                    required
                    value={toPersianNumbers(formData.percentage)}
                    onChange={(e) => {
                      const raw = toEnglishNumbers(e.target.value);
                      if (/^\d*\.?\d*$/.test(raw)) {
                        setFormData({ ...formData, percentage: raw });
                      }
                    }}
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                    placeholder="مثلاً ۴"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    تعداد اقساط پیشنهادی (ماه)
                  </label>
                  <input
                    type="text"
                    required
                    value={toPersianNumbers(formData.installments_count)}
                    onChange={(e) => {
                      const raw = toEnglishNumbers(e.target.value).replace(
                        /\D/g,
                        "",
                      );
                      setFormData({ ...formData, installments_count: raw });
                    }}
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all"
                    placeholder="مثلاً ۱۲"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  توضیحات (اختیاری)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full h-24 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-sm focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 dark:text-white transition-all resize-none"
                  placeholder="توضیحات تکمیلی درخواست وام..."
                />
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-10 rounded-xl font-medium transition-colors"
                >
                  ثبت درخواست
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-10 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
      {isReceiptModalOpen && selectedLoanForReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                ارسال فیش واریزی
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-700/30">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  مبلغ کارمزد:{" "}
                  <strong className="font-bold">
                    {formatCurrency(
                      selectedLoanForReceipt.registration_fee_amount || 0,
                    )}
                  </strong>
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  تصویر فیش
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setReceiptImageFile(e.target.files?.[0] || null)
                  }
                  className="w-full text-sm text-slate-500 dark:text-slate-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-xl file:border-0
                    file:text-sm file:font-semibold
                    file:bg-persian-50 file:text-persian-700
                    hover:file:bg-persian-100
                    dark:file:bg-persian-900/30 dark:file:text-persian-400"
                />
              </div>
              <div className="pt-4 flex gap-3">
                <button
                  onClick={handleUploadReceipt}
                  disabled={!receiptImageFile || uploadingReceipt}
                  className="flex-1 bg-persian-600 hover:bg-persian-700 disabled:opacity-50 text-white h-11 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {uploadingReceipt && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  )}
                  {uploadingReceipt ? "در حال ارسال..." : "ارسال فیش"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsReceiptModalOpen(false)}
                  disabled={uploadingReceipt}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isReviewModalOpen && selectedLoanForReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-transparent flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    بررسی و پاسخ به پیشنهاد مدیریت
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    وام مبلغ {formatCurrency(selectedLoanForReview.amount)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsReviewModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Proposal Highlight Card */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50/50 dark:from-purple-950/40 dark:to-indigo-950/20 border border-purple-200 dark:border-purple-800/60 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-purple-900 dark:text-purple-300 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    شرایط تعیین شده توسط مدیریت:
                  </span>
                  {selectedLoanForReview.loan_type_name && (
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 font-medium">
                      نوع: {selectedLoanForReview.loan_type_name}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1">
                  <div className="bg-white/80 dark:bg-slate-800/80 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/50 text-center">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block mb-0.5">
                      درصد کارمزد
                    </span>
                    <span className="text-base font-bold text-purple-700 dark:text-purple-300">
                      {toPersianNumbers(selectedLoanForReview.percentage ?? 0)}٪
                    </span>
                  </div>
                  <div className="bg-white/80 dark:bg-slate-800/80 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/50 text-center">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block mb-0.5">
                      تعداد اقساط
                    </span>
                    <span className="text-base font-bold text-purple-700 dark:text-purple-300">
                      {toPersianNumbers(
                        selectedLoanForReview.installments_count ?? 0,
                      )}{" "}
                      ماه
                    </span>
                  </div>
                  <div className="col-span-2 sm:col-span-1 bg-white/80 dark:bg-slate-800/80 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/50 text-center">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block mb-0.5">
                      کارمزد اولیه
                    </span>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                      {toPersianNumbers(
                        selectedLoanForReview.registration_fee_percentage ?? 0,
                      )}
                      ٪{" "}
                      <span className="text-[10px] text-slate-500 block font-normal">
                        (
                        {formatCurrency(
                          (Number(selectedLoanForReview.amount) *
                            (selectedLoanForReview.registration_fee_percentage ||
                              0)) /
                            100,
                        )}
                        )
                      </span>
                    </span>
                  </div>
                </div>

                {selectedLoanForReview.admin_notes && (
                  <div className="mt-2 pt-2 border-t border-purple-200/60 dark:border-purple-800/40 text-xs">
                    <span className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      پیام / توضیح مدیر:
                    </span>
                    <p className="text-slate-600 dark:text-slate-400 bg-white/60 dark:bg-slate-900/40 p-2 rounded-lg">
                      {selectedLoanForReview.admin_notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Negotiation History (if available) */}
              {selectedLoanForReview.negotiation_history &&
                (() => {
                  try {
                    const history = JSON.parse(
                      selectedLoanForReview.negotiation_history,
                    );
                    if (Array.isArray(history) && history.length > 1) {
                      return (
                        <div className="space-y-2 border border-slate-200 dark:border-slate-700 rounded-xl p-3 bg-slate-50/50 dark:bg-slate-900/30">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                            <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                            تاریخچه گفتگو و پیشنهادات:
                          </span>
                          <div className="space-y-2 max-h-36 overflow-y-auto pr-1 text-xs">
                            {history.map((item: any, idx: number) => (
                              <div
                                key={idx}
                                className={`p-2 rounded-lg border text-[11px] ${
                                  item.role === "admin"
                                    ? "bg-purple-50/60 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800/40"
                                    : "bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/40"
                                }`}
                              >
                                <div className="flex items-center justify-between font-semibold mb-0.5">
                                  <span
                                    className={
                                      item.role === "admin"
                                        ? "text-purple-700 dark:text-purple-300"
                                        : "text-blue-700 dark:text-blue-300"
                                    }
                                  >
                                    {item.role === "admin"
                                      ? "مدیر سیستم"
                                      : "شما (متقاضی)"}
                                    :
                                  </span>
                                  <span className="text-[10px] text-slate-400">
                                    {item.percentage != null &&
                                      `${toPersianNumbers(item.percentage)}٪`}
                                    {item.installments_count != null &&
                                      ` - ${toPersianNumbers(item.installments_count)} ماه`}
                                  </span>
                                </div>
                                {item.notes && (
                                  <p className="text-slate-600 dark:text-slate-400 mt-1">
                                    {item.notes}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                  } catch (e) {}
                  return null;
                })()}

              {/* Mode Selection Tabs */}
              <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-900/60 rounded-xl">
                <button
                  type="button"
                  onClick={() => setReviewMode("accept")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    reviewMode === "accept"
                      ? "bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 shadow-sm"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>تایید شرایط مدیر</span>
                </button>
                <button
                  type="button"
                  onClick={() => setReviewMode("counter")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    reviewMode === "counter"
                      ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                  }`}
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  <span>پیشنهاد متقابل / تغییر شرایط</span>
                </button>
              </div>

              {/* Accept Mode */}
              {reviewMode === "accept" && (
                <div className="space-y-4">
                  <div className="p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-xl text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
                    با تایید این شرایط، درخواست شما با کارمزد{" "}
                    <strong>
                      {toPersianNumbers(selectedLoanForReview.percentage ?? 0)}٪
                    </strong>{" "}
                    و اقساط{" "}
                    <strong>
                      {toPersianNumbers(
                        selectedLoanForReview.installments_count ?? 0,
                      )}{" "}
                      ماه
                    </strong>{" "}
                    تایید اولیه شده و جهت تایید نهایی برای مدیریت ارسال می‌گردد.
                    پس از تایید نهایی مدیر، وارد مرحله پرداخت کارمزد ثبت نام
                    خواهید شد.
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                      پیام یا یادداشت برای مدیریت (اختیاری):
                    </label>
                    <textarea
                      rows={2}
                      value={acceptNotes}
                      onChange={(e) => setAcceptNotes(e.target.value)}
                      placeholder="مثلا: شرایط مورد تایید است، تشکر"
                      className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 dark:text-white"
                    />
                  </div>

                  <div className="pt-2 flex gap-3">
                    <button
                      type="button"
                      onClick={handleConfirmAccept}
                      disabled={submittingReview}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 rounded-xl font-bold text-xs shadow-md shadow-emerald-900/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {submittingReview ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      <span>تایید نهایی شرایط توسط شما</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsReviewModalOpen(false)}
                      disabled={submittingReview}
                      className="px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl text-xs font-medium transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              )}

              {/* Counter Proposal Mode */}
              {reviewMode === "counter" && (
                <form onSubmit={handleConfirmCounter} className="space-y-4">
                  <div className="p-3.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                    اگر مایل به تغییر تعداد اقساط یا درصد کارمزد هستید، پیشنهاد
                    جدید خود را ثبت کنید تا مجدداً توسط مدیر بررسی و اعلام نظر
                    شود.
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                        کارمزد پیشنهادی شما (۰ تا ۱۵٪)
                      </label>
                      <input
                        type="text"
                        required
                        value={toPersianNumbers(counterData.percentage)}
                        onChange={(e) => {
                          const rawValue = toEnglishNumbers(e.target.value);
                          if (/^\d*\.?\d*$/.test(rawValue)) {
                            const num = parseFloat(rawValue || "0");
                            if (!rawValue || (num >= 0 && num <= 15)) {
                              setCounterData({
                                ...counterData,
                                percentage: rawValue,
                              });
                            }
                          }
                        }}
                        placeholder="مثلا ۴"
                        className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 dark:text-white"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                        اقساط پیشنهادی شما (۱ تا ۳۶ ماه)
                      </label>
                      <input
                        type="text"
                        required
                        value={toPersianNumbers(counterData.installments_count)}
                        onChange={(e) => {
                          const rawValue = toEnglishNumbers(e.target.value);
                          if (/^\d*$/.test(rawValue)) {
                            const num = parseInt(rawValue || "0", 10);
                            if (!rawValue || (num >= 0 && num <= 36)) {
                              setCounterData({
                                ...counterData,
                                installments_count: rawValue,
                              });
                            }
                          }
                        }}
                        placeholder="مثلا ۱۸"
                        className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                      علت یا توضیح پیشنهاد شما به مدیر:
                    </label>
                    <textarea
                      rows={2}
                      value={counterData.notes}
                      onChange={(e) =>
                        setCounterData({
                          ...counterData,
                          notes: e.target.value,
                        })
                      }
                      placeholder="مثلا: در صورت امکان اقساط ۱۸ ماهه باشد تا پرداخت ماهانه سبک‌تر شود."
                      className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 dark:text-white"
                    />
                  </div>

                  <div className="pt-2 flex gap-3">
                    <button
                      type="submit"
                      disabled={submittingReview}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-11 rounded-xl font-bold text-xs shadow-md shadow-blue-900/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {submittingReview ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <ArrowLeftRight className="w-4 h-4" />
                      )}
                      <span>ارسال پیشنهاد متقابل به مدیر</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsReviewModalOpen(false)}
                      disabled={submittingReview}
                      className="px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl text-xs font-medium transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
